import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { TextField, Typography, Typography2 } from '@coursera/cds-core';
import { Box, Notification } from '@coursera/coursera-ui';

import { Button } from 'bundles/enterprise-ui/components/Button';
import { Spacer } from 'bundles/enterprise-ui/components/Spacer';
import { assertNever } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/enterprise-admin-skills';

const NAME_LIMIT = 100;
const DESCRIPTION_LIMIT = 500;

const DuplicateNameNotification: React.FC<{ name: string }> = ({ name }) => (
  <Notification
    type="error"
    message={
      <Typography2 component="p">
        <FormattedMessage
          message={_t(
            'Error: A SkillSet with the name ‘{name}’ already exist in your organization, please choose an alternative.'
          )}
          name={name}
        />
      </Typography2>
    }
    htmlAttributes={{ role: 'alert' }}
  />
);

const ErrorNotification: React.FC<{}> = () => (
  <Notification
    type="error"
    message={
      <Typography2 component="p">
        {_t(
          'Error: Your SkillSet changes could not be saved as errors have been raised, please rectify these errors to save changes.'
        )}
      </Typography2>
    }
    htmlAttributes={{ role: 'alert' }}
  />
);

export const LearningProgramSkillSetChange: React.FC<{}> = () => (
  <Notification
    type="warning"
    message={
      <>
        <Typography2 variant="bodyPrimary" component="strong">
          {_t('IMPORTANT REMINDER')}
        </Typography2>
        <Typography2 component="p">
          {_t(
            `This SkillSet has been added to learning programs. Changes made to this
             SkillSet will be immediately visible to all learners in these programs.`
          )}
        </Typography2>
      </>
    }
  />
);

type Props = {
  name?: string;
  validateName: (value: string) => boolean;
  description?: string;
  programs?: ({
    id: string;
    metadata: {
      name: string;
    };
  } | null)[];
  onCancel: () => void;
  onSave: (skillSet: { name?: string; description?: string }) => void;
};

type State = {
  name: string;
  description: string;
  nameError?: 'conflict' | 'empty' | 'long';
  descriptionError?: 'long';
};

export default class SkillSetEditor extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      name: props.name ?? '',
      description: props.description ?? '',
    };
  }

  handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ name: event.target.value, nameError: undefined });
  };

  handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ description: event.target.value });
  };

  handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { onSave, validateName } = this.props;
    const { name, description } = this.state;
    const trimmedName = name.trim();
    if (!trimmedName) {
      this.setState({ nameError: 'empty' });
      return;
    }
    if (!validateName(trimmedName)) {
      this.setState({ nameError: 'conflict' });
      return;
    }
    if (trimmedName.length > NAME_LIMIT) {
      this.setState({ nameError: 'long' });
      return;
    }
    const trimmedDescription = description.trim();
    if (trimmedDescription.length > DESCRIPTION_LIMIT) {
      this.setState({ descriptionError: 'long' });
      return;
    }
    onSave({ name: trimmedName, description: trimmedDescription });
  };

  getNameErrorMessage(): string | undefined {
    const { nameError, name } = this.state;
    switch (nameError) {
      case 'conflict':
        return _t('Error: Please enter an alternative SkillSet name');
      case 'empty':
        return _t('Error: Please enter a SkillSet name');
      case 'long':
        return _t('Error: Your SkillSet name is #{count} characters and exceeds the character limit', {
          count: name.length,
        });
      case undefined:
        break;
      default:
        assertNever(nameError);
    }
    return undefined;
  }

  getDescriptionErrorMessage(): string | undefined {
    const { descriptionError, description } = this.state;
    switch (descriptionError) {
      case 'long':
        return _t('Error: Your SkillSet description is #{count} characters and exceeds the character limit', {
          count: description.length,
        });
      case undefined:
        break;
      default:
        assertNever(descriptionError);
    }
    return undefined;
  }

  render() {
    const { onCancel, programs } = this.props;
    const { name, description, nameError, descriptionError } = this.state;

    return (
      <form onSubmit={this.handleSubmit} data-e2e="skillset-editor">
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography variant="h1" component="h2">
          {_t('Edit SkillSet')}
        </Typography>
        <Spacer size={12} />
        <Typography2 component="p">
          {_t('Customize the name and description of this SkillSet to make it more relevant for your learners')}
        </Typography2>
        <Spacer size={24} />
        <Typography2 component="p">
          <strong>{_t('Tip: ')}</strong>
          {_t('You can fully edit a SkillSet’s skills and target levels once it has been added to your organization.')}
        </Typography2>
        {programs !== undefined && programs.length > 0 && (
          <div>
            <Spacer size={24} />
            <LearningProgramSkillSetChange />
          </div>
        )}
        <Spacer size={24} />
        {(nameError || descriptionError) && (
          <div>
            {nameError === 'conflict' ? <DuplicateNameNotification name={name} /> : <ErrorNotification />}
            <Spacer size={24} />
          </div>
        )}
        <TextField
          fullWidth
          label={_t('SkillSet name')}
          supportText={_t('Max. #{count} Characters', { count: NAME_LIMIT })}
          value={name}
          onChange={this.handleNameChange}
          validationLabel={this.getNameErrorMessage()}
          validationStatus={this.getNameErrorMessage() ? 'error' : undefined}
          maxLength={NAME_LIMIT}
          data-e2e="skillSet_name"
        />
        <Spacer size={12} />
        <TextField
          fullWidth
          multiline
          label={_t('SkillSet description')}
          supportText={_t('Max. #{count} Characters', { count: DESCRIPTION_LIMIT })}
          value={description}
          onChange={this.handleDescriptionChange}
          validationLabel={this.getDescriptionErrorMessage()}
          validationStatus={this.getDescriptionErrorMessage() ? 'error' : undefined}
          maxLength={DESCRIPTION_LIMIT}
          data-e2e="skillSet_description"
        />
        <Spacer size={12} />
        <Box flexDirection="row-reverse">
          <Button variant="primary" type="submit">
            {_t('Save changes')}
          </Button>
          <Spacer size={12} />
          <Button variant="secondary" onClick={onCancel}>
            {_t('Cancel')}
          </Button>
        </Box>
      </form>
    );
  }
}
